import { useAtom } from 'jotai/index';
import { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import useInputState from './UseInputState';
import {
  addressSaveAtom, isPartnersInputsDirtyAtom,
  selectedAddressIdAtom,
  selectedPartnerAddressAtom,
  selectedPartnerAtom,
} from '../atoms/partnersAtoms';
import { maxAddressManagersPhonesAtom } from '../atoms/globalAtoms';
import PartnersServices from '../services/PartnersServices';
import deepClone from '../helpers/deepClone';
import { DEFAULT_WEEK_DAYS } from '../constants/addresses';
import sleep from '../helpers/sleep';
import convertSchedule from '../helpers/convertSchedule';
import { DATE_FORMAT } from '../constants';
import deepEqual from '../helpers/deepEqual';
import {
  ADDRESS_PHONE_MAIN_MAX_LENGTH,
  getAddressPhoneError,
  parseAddressPhoneFromServer,
} from '../helpers/addressPhone';

const UseSingleAddressInputs = ({ saveAddress }) => {
  const [active, setActive, activeError, setActiveError] = useInputState();
  const [name, setName, nameError, setNameError] = useInputState();
  const [address, setAddress, addressError, setAddressError] = useInputState();
  const [phonePrefix, setPhonePrefix] = useState('');
  const [phoneMain, setPhoneMain] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [hostessLogin, setHostessLogin] = useState('');
  const [hostessLoginCopied, setHostessLoginCopied] = useState(false);

  const [weekDays, setWeekDays] = useState(deepClone(DEFAULT_WEEK_DAYS));
  const [weekDaysError, setWeekDaysError] = useState('');

  const [holidays, setHolidays] = useState([]);
  const [holidayInputs, setHolidayInputs, holidayInputsError, setHolidayInputsError] = useInputState({});

  const [licenseDay, setLicenseDay, licenseDayError, setLicenseDayError] = useInputState(null);

  const [additionalPhones, setAdditionalPhones] = useState([]);

  const [addressSave, setAddressSave] = useAtom(addressSaveAtom);
  const [selectedPartner] = useAtom(selectedPartnerAtom);
  const [selectedAddress] = useAtom(selectedPartnerAddressAtom);
  const [selectedAddressId] = useAtom(selectedAddressIdAtom);
  const isNewAddress = selectedAddressId === -1;
  const [maxAddressManagersPhones] = useAtom(maxAddressManagersPhonesAtom);
  const [, setIsPartnersInputsDirty] = useAtom(isPartnersInputsDirtyAtom);
  const baselinePayloadRef = useRef(null);

  const normalizeDate = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date) {
      return moment(dateValue).format('YYYY-MM-DD HH:mm:ss');
    }
    if (typeof dateValue === 'string') {
      const parsed = moment(dateValue);
      if (parsed.isValid()) return parsed.format('YYYY-MM-DD HH:mm:ss');
    }
    return null;
  };

  const buildComparablePayload = ({
    partnerId,
    id,
    active: activeValue,
    name: nameValue,
    address: addressValue,
    phonePrefix: phonePrefixValue,
    phoneMain: phoneMainValue,
    weekDays: weekDaysValue,
    holidays: holidaysValue,
    licenseDay: licenseDayValue,
    additionalPhones: additionalPhonesValue,
  }) => {
    const payload = PartnersServices.convertAddressSavingPayload({
      partnerId,
      id,
      active: activeValue,
      name: nameValue,
      address: addressValue,
      phonePrefix: phonePrefixValue,
      phoneMain: phoneMainValue,
      weekDays: weekDaysValue,
      holidays: holidaysValue,
      licenseDay: licenseDayValue,
      additionalPhones: additionalPhonesValue,
    });
    delete payload.partnerId;
    payload.schedule = convertSchedule(payload.schedule);
    payload.licence_expires_at = normalizeDate(payload.licence_expires_at);
    return payload;
  };

  const checkForErrors = () => {
    let isError = false;
    if (!active) {
      setActiveError('Укажите статус');
      isError = true;
    }
    if (!name) {
      setNameError('Укажите название');
      isError = true;
    }
    if (!address) {
      setAddressError('Укажите адрес');
      isError = true;
    }
    const phoneValidationError = getAddressPhoneError(phonePrefix, phoneMain);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      isError = true;
    }

    const times = weekDays.filter((i) => !i.day_off).reduce((acc, i) => [...acc, i.open_time, i.close_time], []);
    // eslint-disable-next-line no-restricted-syntax
    for (const time of times) {
      if (!moment(time, 'HH:mm', true).isValid()) {
        setWeekDaysError('Некоторые поля заполнены неправильно');
        isError = true;
        break;
      }
    }

    return isError;
  };

  const handlePhonePrefix = (ev) => {
    const digits = String(ev?.target?.value ?? '').replace(/\D/g, '').slice(0, 5);
    setPhonePrefix(digits);
    if (phoneError) setPhoneError('');
  };

  const handlePhoneMain = (ev) => {
    const digits = String(ev?.target?.value ?? '').replace(/\D/g, '').slice(0, ADDRESS_PHONE_MAIN_MAX_LENGTH);
    setPhoneMain(digits);
    if (phoneError) setPhoneError('');
  };

  const handleWeekDay = (index, field) => (ev) => {
    weekDays[index][field] = field === 'day_off' ? !weekDays[index][field] : ev.target.value;
    setWeekDays([...weekDays]);
    setWeekDaysError('');
  };
  const handleHolidayInputs = (field) => (ev) => {
    holidayInputs[field] = field === 'day_off' ? !holidayInputs[field] : ev.target.value;
    setHolidayInputs({ ...holidayInputs }, true);
  };

  const onClipboardClick = async () => {
    setHostessLoginCopied(true);
    await navigator.clipboard.writeText(hostessLogin);
    await sleep(1000);
    setHostessLoginCopied(false);
  };

  const onDeleteHolidayClick = (index) => () => {
    const newHolidays = holidays.filter((_, i) => i !== index);
    setHolidays([...newHolidays]);
  };

  const setAdditionalPhoneAt = (index) => (ev) => {
    const next = [...additionalPhones];
    next[index] = typeof ev?.target?.value === 'string' ? ev.target.value : ev;
    setAdditionalPhones(next);
  };

  const onAddAdditionalPhoneClick = () => {
    if (!maxAddressManagersPhones || additionalPhones.length >= maxAddressManagersPhones) return;
    setAdditionalPhones([...additionalPhones, '']);
  };

  const onRemoveAdditionalPhoneClick = (index) => () => {
    setAdditionalPhones(additionalPhones.filter((_, i) => i !== index));
  };

  const onAddHolidayClick = () => {
    let error = false;
    if (!holidayInputs.date) {
      setHolidayInputsError('Укажите дату');
      error = true;
    } else if (!moment(holidayInputs.date, 'DD.MM.YYYY', true).isValid()) {
      setHolidayInputsError('Невалидная дата');
      error = true;
    } else if (!holidayInputs.day_off) {
      if (!moment(holidayInputs.open_time, 'HH:mm', true).isValid()) {
        setHolidayInputsError('Невалидное время открытия');
        error = true;
      } else if (!moment(holidayInputs.close_time, 'HH:mm', true).isValid()) {
        setHolidayInputsError('Невалидное время закрытия');
        error = true;
      }
    }
    if (error) return;

    const newHolidays = holidays.filter((i) => i.date !== holidayInputs.date);

    newHolidays.unshift({
      // date: PartnersServices.convertDateFormat(holidayInputs.date, 'DD.MM.YYYY', 'YYYY-MM-DD'),
      date: holidayInputs.date,
      open_time: holidayInputs.open_time,
      close_time: holidayInputs.close_time,
      day_off: holidayInputs.day_off,
    });

    setHolidays([...newHolidays]);
    setHolidayInputs({}, true);
  };

  useEffect(() => {
    if (!addressSave || !selectedPartner) return;
    if (!isNewAddress && !selectedAddress) return;

    if (checkForErrors()) {
      setAddressSave(0);
      return;
    }

    saveAddress(PartnersServices.convertAddressSavingPayload({
      partnerId: selectedPartner.info.id,
      id: isNewAddress ? undefined : selectedAddress?.id,
      active,
      name,
      address,
      phonePrefix,
      phoneMain,
      weekDays,
      holidays,
      licenseDay,
      additionalPhones,
    }));
    setAddressSave(0);
  }, [
    addressSave,
    selectedPartner,
    selectedAddress,
    isNewAddress,
    active,
    name,
    address,
    phonePrefix,
    phoneMain,
    weekDays,
    holidays,
    licenseDay,
    additionalPhones,
    saveAddress,
    setAddressSave,
  ]);

  useEffect(() => {
    if (!selectedPartner) {
      baselinePayloadRef.current = null;
      return;
    }

    if (isNewAddress) {
      if (selectedAddress) return;

      const activeValue = { value: 'active', label: 'Активен' };
      const licenseDayValue = new Date();
      const weekDaysValue = deepClone(DEFAULT_WEEK_DAYS);

      setActive(activeValue, true);
      setName('', true);
      setAddress('', true);
      setPhonePrefix('');
      setPhoneMain('');
      setPhoneError('');
      setHostessLogin('');
      setWeekDays(weekDaysValue);
      setHolidays([]);
      setLicenseDay(licenseDayValue, true);
      setAdditionalPhones([]);

      baselinePayloadRef.current = buildComparablePayload({
        partnerId: selectedPartner.info.id,
        id: undefined,
        active: activeValue,
        name: '',
        address: '',
        phonePrefix: '',
        phoneMain: '',
        weekDays: weekDaysValue,
        holidays: [],
        licenseDay: licenseDayValue,
        additionalPhones: [],
      });
      setIsPartnersInputsDirty(false);
      return;
    }

    if (!selectedAddress) {
      baselinePayloadRef.current = null;
      return;
    }

    const activeValue = selectedAddress.active
      ? { value: 'active', label: 'Активен' }
      : { value: 'nonActive', label: 'Заблокирован' };
    const nameValue = selectedAddress.name || '';
    const addressValue = selectedAddress.descr || '';
    const { prefix, main } = parseAddressPhoneFromServer({
      phone: selectedAddress.phone,
      phone_prefixed: selectedAddress.phone_prefixed,
    });
    const selectedAddressWeekDays = selectedAddress.schedule
      .filter((i) => Boolean(i.weekday))
      .map((i) => ({
        ...i,
        open_time: PartnersServices.convertSecondsToFormat(i.open_time),
        close_time: PartnersServices.convertSecondsToFormat(i.close_time),
      }));
    const selectedAddressHolidays = selectedAddress.schedule
      .filter((i) => Boolean(i.date))
      .map((i) => ({
        ...i,
        date: PartnersServices.convertDateFormat(i.date, 'YYYY-MM-DD', 'DD.MM.YYYY'),
        open_time: PartnersServices.convertSecondsToFormat(i.open_time),
        close_time: PartnersServices.convertSecondsToFormat(i.close_time),
      }));
    const licenseDayValue = selectedAddress.licence_expires_at
      ? new Date(moment(selectedAddress.licence_expires_at, DATE_FORMAT))
      : new Date();
    const fromServer = selectedAddress.managers_phones || selectedAddress.adress?.managers_phones;
    const maxPhones = Number.isFinite(Number(maxAddressManagersPhones)) && Number(maxAddressManagersPhones) > 0
      ? Number(maxAddressManagersPhones)
      : 0;
    const additionalPhonesValue = maxPhones > 0 && Array.isArray(fromServer) && fromServer.length
      ? fromServer.map((p) => String(p ?? '')).slice(0, maxPhones)
      : [];

    setActive(activeValue, true);
    setName(nameValue, true);
    setAddress(addressValue, true);
    setPhonePrefix(prefix);
    setPhoneMain(main);
    setPhoneError('');
    setHostessLogin(selectedAddress.login);
    setWeekDays(selectedAddressWeekDays);
    setHolidays(selectedAddressHolidays);
    setLicenseDay(licenseDayValue, true);
    setAdditionalPhones(additionalPhonesValue);

    baselinePayloadRef.current = buildComparablePayload({
      partnerId: selectedPartner.info.id,
      id: selectedAddress?.id,
      active: activeValue,
      name: nameValue,
      address: addressValue,
      phonePrefix: prefix,
      phoneMain: main,
      weekDays: selectedAddressWeekDays,
      holidays: selectedAddressHolidays,
      licenseDay: licenseDayValue,
      additionalPhones: additionalPhonesValue,
    });
    setIsPartnersInputsDirty(false);
  }, [selectedAddress, selectedPartner, selectedAddressId, isNewAddress, maxAddressManagersPhones, setIsPartnersInputsDirty]);

  useEffect(() => {
    if (!selectedPartner || !baselinePayloadRef.current) {
      setIsPartnersInputsDirty(false);
      return;
    }

    if (!isNewAddress && !selectedAddress) {
      setIsPartnersInputsDirty(false);
      return;
    }

    const currentPayload = buildComparablePayload({
      partnerId: selectedPartner.info.id,
      id: isNewAddress ? undefined : selectedAddress?.id,
      active,
      name,
      address,
      phonePrefix,
      phoneMain,
      weekDays,
      holidays,
      licenseDay,
      additionalPhones,
    });

    const isDirty = !deepEqual(currentPayload, baselinePayloadRef.current);
    setIsPartnersInputsDirty(isDirty);
  }, [
    selectedAddress,
    selectedPartner,
    isNewAddress,
    active,
    name,
    address,
    phonePrefix,
    phoneMain,
    weekDays,
    holidays,
    licenseDay,
    additionalPhones,
    setIsPartnersInputsDirty,
  ]);

  return {
    values: {
      active,
      name,
      address,
      phonePrefix,
      phoneMain,
      phoneMainMaxLength: ADDRESS_PHONE_MAIN_MAX_LENGTH,
      hostessLogin,
      hostessLoginCopied,
      weekDays,
      holidays,
      holidayInputs,
      licenseDay,
      additionalPhones,
      maxAdditionalPhones: maxAddressManagersPhones,
    },
    setters: {
      active: setActive,
      name: setName,
      address: setAddress,
      phonePrefix: handlePhonePrefix,
      phoneMain: handlePhoneMain,
      weekDays: handleWeekDay,
      holidayInputs: handleHolidayInputs,
      licenseDay: setLicenseDay,
    },
    errors: {
      active: activeError,
      name: nameError,
      address: addressError,
      phone: phoneError,
      weekDays: weekDaysError,
      holidayInputs: holidayInputsError,
      licenseDay: licenseDayError
    },
    events: {
      onClipboardClick,
      onAddHolidayClick,
      onDeleteHolidayClick,
      onAddAdditionalPhoneClick,
      onRemoveAdditionalPhoneClick,
      setAdditionalPhoneAt,
    },
  };
};

export default UseSingleAddressInputs;
